import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import toast from 'react-hot-toast';
import { api } from '../App';
import 'leaflet/dist/leaflet.css';
import StormRoofAge, { WIN_TONE } from '../components/storms/StormRoofAge';

function RoofBadge({ i, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const age = i.roof_age_years ?? i.roof_age;
  const check = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.get('/api/storms/roof-lookup', { params: { address: i.address, city: i.city || '', state: i.state || '', zip: i.zip || '', lead_id: i.id } });
      onUpdate({ roof_age_years: r.data.roof_age_years, roof_window: r.data.roof_window, roof_checked: true });
      if (r.data.roof_age_years == null) toast('No roof age on record for this home');
    } catch (err) { toast.error(err.response?.data?.detail || 'Roof-age lookup failed'); }
    setBusy(false);
  };
  if (age != null) {
    return <span className={`badge border ${WIN_TONE[i.roof_window] || WIN_TONE.unknown}`} data-testid={`impacted-roof-age-${i.id}`}><i className="fa-solid fa-house-chimney mr-1" />Roof {age} yrs{i.roof_window && i.roof_window !== 'unknown' ? ` · ${i.roof_window}` : ''}</span>;
  }
  if (i.roof_checked) return <span className="badge bg-slate-100 text-slate-600 border-slate-200" data-testid={`impacted-roof-age-${i.id}`}>Roof age unknown</span>;
  return (
    <button onClick={check} disabled={busy} className="badge bg-surface-alt text-text-secondary border-border hover:border-primary" data-testid={`impacted-roof-check-${i.id}`}>
      {busy ? <i className="fa-solid fa-spinner fa-spin mr-1" /> : <i className="fa-solid fa-house-chimney-crack mr-1" />}Check roof age
    </button>
  );
}

const SEV = { Extreme: 'bg-rose-600 text-white border-rose-700', Severe: 'bg-orange-500 text-white border-orange-600', Moderate: 'bg-amber-400 text-slate-900 border-amber-500', Minor: 'bg-sky-100 text-sky-800 border-sky-200', Unknown: 'bg-slate-100 text-slate-600 border-slate-200' };
const CAT = { roof_damage: { label: 'Roof damage risk', icon: 'fa-house-chimney-crack', tone: 'text-rose-600' }, site_safety: { label: 'Job-site safety', icon: 'fa-helmet-safety', tone: 'text-amber-600' } };
const STATUS = { go: 'bg-emerald-500', caution: 'bg-amber-400', hold: 'bg-rose-500' };

function Stat({ label, value, sub, tone = 'text-text-primary', testid }) {
  return (
    <div className="card-interactive p-4" data-testid={testid}>
      <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{label}</span>
      <p className={`font-heading text-2xl font-bold mt-1 tabular-nums ${tone}`}>{value}</p>
      {sub && <p className="text-xs text-text-muted mt-0.5">{sub}</p>}
    </div>
  );
}

function Hurricanes({ storms }) {
  if (!storms?.length) return <div className="p-4 rounded-xl border border-border bg-surface text-sm text-text-muted" data-testid="no-hurricanes"><i className="fa-solid fa-hurricane mr-2 text-sky-500" />No active tropical cyclones on NHC's board right now.</div>;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="hurricane-list">
      {storms.map(s => (
        <div key={s.id} className="p-4 rounded-xl border border-rose-200 bg-rose-50/60" data-testid={`hurricane-${s.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><i className="fa-solid fa-hurricane text-rose-600 text-lg animate-spin" style={{ animationDuration: '6s' }} /><b className="text-text-primary">{s.classification} {s.name}</b></div>
            <span className="badge bg-rose-600 text-white border-rose-700">{s.category ? `Cat ${s.category}` : `${s.wind_mph} mph`}</span>
          </div>
          <p className="text-xs text-text-muted mt-1">{s.basin} · {s.wind_mph} mph winds · {s.pressure_mb} mb · moving {s.movement_dir}° at {s.movement_mph} mph · {s.lat}, {s.lng}</p>
          <div className="flex gap-3 mt-2 text-xs">{s.advisory_url && <a href={s.advisory_url} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline">Public advisory</a>}{s.graphics_url && <a href={s.graphics_url} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline">Cone & track</a>}</div>
        </div>
      ))}
    </div>
  );
}

function AlertRow({ a, onPull }) {
  const c = CAT[a.category] || CAT.site_safety;
  return (
    <div className="p-4 rounded-xl border border-border bg-surface hover:border-primary/30 transition-colors" data-testid={`alert-${a.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`badge ${SEV[a.severity] || SEV.Unknown}`}>{a.severity}</span>
        <b className="text-sm text-text-primary">{a.event}</b>
        <span className={`text-xs font-semibold ${c.tone}`}><i className={`fa-solid ${c.icon} mr-1`} />{c.label}</span>
        <span className="ml-auto text-xs text-text-muted">{a.states.join(', ')} · until {a.ends ? new Date(a.ends).toLocaleString() : '—'}</span>
      </div>
      <p className="text-xs text-text-muted mt-1.5 line-clamp-2">{a.area}</p>
      <div className="flex flex-wrap gap-2 mt-2">
        {a.states.slice(0, 3).map(s => <button key={s} onClick={() => onPull(s)} className="btn-secondary text-[11px] py-1 px-3" data-testid={`pull-leads-${a.id}-${s}`}><i className="fa-solid fa-download mr-1" />Pull leads in {s}</button>)}
      </div>
    </div>
  );
}

function SitePlan({ plan }) {
  if (!plan) return null;
  return (
    <div className="space-y-3" data-testid="site-plan">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <b className="text-text-primary">{plan.label || `${plan.lat?.toFixed(3)}, ${plan.lng?.toFixed(3)}`}</b>
        <span className="badge bg-emerald-50 text-emerald-700 border-emerald-200">{plan.go_days} go</span>
        <span className="badge bg-amber-50 text-amber-700 border-amber-200">{plan.caution_days} caution</span>
        <span className="badge bg-rose-50 text-rose-700 border-rose-200">{plan.hold_days} hold</span>
      </div>
      <p className="text-xs text-text-muted" data-testid="crew-note">{plan.crew_note}</p>
      {plan.active_alerts?.length > 0 && <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">{plan.active_alerts.map(a => <div key={a.event}><b>{a.event}</b> ({a.severity}) — {a.headline}</div>)}</div>}
      <div className="grid grid-cols-7 gap-2">
        {plan.days.map(d => (
          <div key={d.date} className="p-2.5 rounded-xl border border-border bg-surface text-center" title={d.reasons.join('\n')} data-testid={`plan-day-${d.date}`}>
            <span className={`block w-2.5 h-2.5 rounded-full mx-auto ${STATUS[d.status]}`} />
            <p className="text-[10px] font-bold text-text-muted mt-1">{new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short' })}</p>
            <p className="text-xs font-semibold text-text-primary">{Math.round(d.temp_max)}°</p>
            <p className="text-[10px] text-text-muted leading-tight">{d.summary}</p>
            <p className="text-[10px] text-text-muted"><i className="fa-solid fa-wind" /> {Math.round(d.gust_mph)} · <i className="fa-solid fa-droplet" /> {d.rain_prob}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StormCenter() {
  const [ov, setOv] = useState(null);
  const [impact, setImpact] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [state, setState] = useState('');
  const [cat, setCat] = useState('');
  const [tab, setTab] = useState('alerts');
  const [selected, setSelected] = useState({});
  const [template, setTemplate] = useState('storm_check');
  const [channel, setChannel] = useState('sms');
  const [sending, setSending] = useState(false);
  const [pulling, setPulling] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planAddr, setPlanAddr] = useState('');
  const [projects, setProjects] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, i, t] = await Promise.all([
        api.get('/api/storms/overview', { params: { state: state || undefined, category: cat || undefined } }),
        api.get('/api/storms/impact'),
        api.get('/api/storms/templates'),
      ]);
      setOv(o.data); setImpact(i.data); setTemplates(t.data.templates);
    } catch (e) { toast.error(e.response?.data?.detail || 'Weather feeds unavailable'); }
    finally { setLoading(false); }
  }, [state, cat]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/api/storms/outreach/history').then(r => setHistory(r.data.items)).catch(() => {}); }, [sending]);

  const pull = async (st) => {
    setPulling(st);
    try {
      const r = await api.post('/api/scanner/scan-and-save', { location: st, count: 10 });
      toast.success(`${r.data.saved_count ?? r.data.count ?? 0} leads pulled in ${st} — re-checking storm impact`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Lead pull failed'); }
    finally { setPulling(null); }
  };

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectAll = () => setSelected(Object.fromEntries((impact?.impacted || []).map(i => [i.id, true])));
  const ids = Object.keys(selected).filter(k => selected[k]);

  const send = async () => {
    if (!ids.length) { toast.error('Select at least one homeowner'); return; }
    setSending(true);
    try {
      const ev = impact.impacted.find(i => selected[i.id])?.top_event || 'storm';
      const r = await api.post('/api/storms/outreach', { lead_ids: ids, template, channel, event: ev });
      toast.success(`${r.data.queued} messages queued${r.data.simulated ? ` (${r.data.simulated} SIMULATED)` : ''} · ${r.data.skipped_no_contact} skipped (no contact)`, { duration: 7000 });
      setSelected({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Outreach failed'); }
    finally { setSending(false); }
  };

  const getPlan = async (params) => {
    try { const r = await api.get('/api/storms/site-plan', { params }); setPlan(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Forecast unavailable'); }
  };
  const planFromAddress = async () => {
    if (!planAddr.trim()) return;
    const m = planAddr.match(/^\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*$/);
    if (m) return getPlan({ lat: m[1], lng: m[2] });
    try {
      const g = await api.get('/api/storms/geocode', { params: { address: planAddr } });
      return getPlan({ lat: g.data.lat, lng: g.data.lng });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not locate that address'); }
  };
  const loadProjects = async () => {
    try { const r = await api.get('/api/storms/site-plan/projects'); setProjects(r.data.projects); }
    catch (e) { toast.error('Could not load project forecasts'); }
  };
  useEffect(() => { if (tab === 'siteplan' && projects === null) loadProjects(); }, [tab, projects]);

  const impacted = impact?.impacted || [];
  const mapPts = [...impacted.map(i => ({ ...i, kind: 'lead' })), ...(ov?.hurricanes || []).map(h => ({ id: h.id, lat: h.lat, lng: h.lng, kind: 'storm', name: `${h.classification} ${h.name}`, top_severity: 'Extreme' }))];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="storm-center-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">Weather Intelligence</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Storm Center</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">Live NWS severe-weather alerts and NHC hurricanes, matched to your homeowners and install sites. Message owners in hit areas, and plan crews around the weather.</p>
        </div>
        <div className="flex items-center gap-2">
          <input value={state} onChange={e => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="State (e.g. FL)" className="input-field w-32" data-testid="storm-state-filter" />
          <select value={cat} onChange={e => setCat(e.target.value)} className="input-field w-44" data-testid="storm-category-filter">
            <option value="">All categories</option><option value="roof_damage">Roof damage risk</option><option value="site_safety">Job-site safety</option>
          </select>
          <button onClick={load} className="btn-secondary" data-testid="storm-refresh-btn"><i className={`fa-solid fa-rotate mr-2 ${loading ? 'fa-spin' : ''}`} />Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Active alerts" value={ov?.total ?? '—'} sub={ov ? `${Object.keys(ov.by_state).length} states` : ''} testid="stat-alerts" />
        <Stat label="Roof-damage alerts" value={ov?.roof_damage ?? '—'} tone="text-rose-600" sub="wind · hail · flood · tornado" testid="stat-roof" />
        <Stat label="Site-safety alerts" value={ov?.site_safety ?? '—'} tone="text-amber-600" sub="heat · fire · fog · freeze" testid="stat-safety" />
        <Stat label="Hurricanes / TS" value={ov?.hurricanes?.length ?? '—'} tone="text-sky-600" sub="NHC active storms" testid="stat-hurricanes" />
        <Stat label="Your homeowners hit" value={impact?.total_impacted ?? '—'} tone="text-primary" sub={impact ? `${impact.leads_checked} leads checked` : ''} testid="stat-impacted" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 card-interactive overflow-hidden" style={{ height: 380 }} data-testid="storm-map">
          <MapContainer center={[36, -96]} zoom={4} style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            {mapPts.filter(p => p.lat && p.lng).map(p => (
              <CircleMarker key={p.kind + p.id} center={[p.lat, p.lng]} radius={p.kind === 'storm' ? 14 : 7}
                pathOptions={{ color: p.kind === 'storm' ? '#e11d48' : p.top_category === 'roof_damage' ? '#f97316' : '#f59e0b', fillColor: p.kind === 'storm' ? '#e11d48' : '#f97316', fillOpacity: 0.55 }}>
                <Popup>{p.kind === 'storm' ? <b>{p.name}</b> : <div><b>{p.name}</b><br />{p.address}, {p.city} {p.state}<br />{p.top_event} · {p.top_severity}</div>}</Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <div className="lg:col-span-2 space-y-3">
          <h3 className="font-heading font-bold text-text-primary"><i className="fa-solid fa-hurricane mr-2 text-rose-500" />Tropical cyclones</h3>
          <Hurricanes storms={ov?.hurricanes} />
          {ov && <div className="p-4 rounded-xl border border-border bg-surface"><p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Alerts by state</p>
            <div className="flex flex-wrap gap-1.5">{Object.entries(ov.by_state).sort((a, b) => b[1] - a[1]).map(([s, n]) => <button key={s} onClick={() => setState(s)} className="badge bg-surface-alt text-text-secondary border-border hover:border-primary" data-testid={`state-chip-${s}`}>{s} <b className="ml-1">{n}</b></button>)}</div></div>}
        </div>
      </div>

      <div className="flex gap-1 border-b border-border">
        {[['alerts', 'Live alerts'], ['impact', `Homeowners in hit areas (${impact?.total_impacted ?? 0})`], ['roofage', 'Roof-age address search'], ['siteplan', 'Job-site weather plan'], ['history', `Outreach log (${history.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 text-sm font-medium transition-all border-b-2 ${tab === k ? 'text-primary border-primary' : 'text-text-muted border-transparent hover:text-text-primary'}`} data-testid={`storm-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === 'alerts' && (
        <div className="space-y-3" data-testid="alerts-list">
          {loading && <p className="text-sm text-text-muted">Loading NWS feed…</p>}
          {!loading && !ov?.alerts?.length && <p className="text-sm text-text-muted">No roof-relevant alerts for this filter right now.</p>}
          {ov?.alerts?.slice(0, 80).map(a => <AlertRow key={a.id} a={a} onPull={pull} />)}
          {pulling && <p className="text-xs text-primary"><i className="fa-solid fa-spinner fa-spin mr-1" />Pulling homeowners in {pulling}…</p>}
        </div>
      )}

      {tab === 'impact' && (
        <div className="space-y-4" data-testid="impact-panel">
          <div className="p-4 rounded-2xl border border-border bg-surface flex flex-wrap items-end gap-3">
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Message</label>
              <select value={template} onChange={e => setTemplate(e.target.value)} className="input-field w-64" data-testid="outreach-template">{templates.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Channel</label>
              <select value={channel} onChange={e => setChannel(e.target.value)} className="input-field w-40" data-testid="outreach-channel"><option value="sms">Text (SMS)</option><option value="email">Email</option><option value="call_list">Call list</option></select></div>
            <button onClick={selectAll} className="btn-secondary" data-testid="select-all-impacted">Select all</button>
            <button onClick={send} disabled={sending || !ids.length} className="btn-primary disabled:opacity-50" data-testid="send-outreach-btn">{sending ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-paper-plane mr-2" />Message {ids.length || ''} owner{ids.length === 1 ? '' : 's'}</>}</button>
            <p className="text-[11px] text-text-muted w-full">{templates.find(t => t.key === template)?.body}</p>
          </div>
          {impact && !impacted.length && (
            <div className="p-6 rounded-2xl border border-dashed border-border text-center text-sm text-text-muted" data-testid="no-impact">
              <i className="fa-solid fa-cloud-sun text-3xl text-sky-400 mb-2 block" />
              None of your {impact.leads_checked} geocoded homeowners sit inside an active alert right now. Use <b>Pull leads in [state]</b> on any alert to bring in owners from a hit market, then message them here.
              {impact.projects_in_alert_areas?.length > 0 && <p className="mt-2 text-amber-600">{impact.projects_in_alert_areas.length} install project(s) are in alert states — check the job-site plan.</p>}
            </div>
          )}
          {impacted.map(i => (
            <label key={i.id} className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${selected[i.id] ? 'border-primary bg-primary/5' : 'border-border bg-surface'}`} data-testid={`impacted-${i.id}`}>
              <input type="checkbox" checked={!!selected[i.id]} onChange={() => toggle(i.id)} className="mt-1" data-testid={`impacted-check-${i.id}`} />
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2"><b className="text-sm text-text-primary">{i.name}</b><span className={`badge ${SEV[i.top_severity]}`}>{i.top_severity}</span><span className="text-xs font-semibold text-rose-600">{i.top_event}</span>
                  {i.existing_solar && <span className="badge bg-sky-50 text-sky-700 border-sky-200">has solar — service lead</span>}
                  <RoofBadge i={i} onUpdate={(patch) => setImpact(im => ({ ...im, impacted: im.impacted.map(x => x.id === i.id ? { ...x, ...patch } : x) }))} />
                  <span className="ml-auto text-xs text-text-muted">score {i.score}</span></div>
                <p className="text-xs text-text-muted mt-0.5">{i.address}, {i.city}, {i.state} {i.zip} · {i.phone || 'no phone'} · {i.email || 'no email'}</p>
                <div className="flex gap-2 mt-1.5 text-[11px]"><Link to={`/leads/${i.id}`} className="text-primary font-semibold hover:underline">Open lead</Link>{!i.phone && <Link to="/skip-trace" className="text-amber-600 font-semibold hover:underline">Skip-trace for phone</Link>}</div>
              </div>
            </label>
          ))}
        </div>
      )}

      {tab === 'roofage' && <StormRoofAge />}

      {tab === 'siteplan' && (
        <div className="space-y-5" data-testid="siteplan-panel">
          <div className="p-4 rounded-2xl border border-border bg-surface flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]"><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Job site (address or lat, lng)</label>
              <input value={planAddr} onChange={e => setPlanAddr(e.target.value)} placeholder="5 Saddle Ct, Freehold NJ  ·  or  40.2148, -74.2783" className="input-field" data-testid="siteplan-input" /></div>
            <button onClick={planFromAddress} className="btn-primary" data-testid="siteplan-run-btn"><i className="fa-solid fa-cloud-sun-rain mr-2" />7-day crew plan</button>
          </div>
          {plan && <div className="p-5 rounded-2xl border border-border bg-surface"><SitePlan plan={plan} /></div>}
          <div>
            <h3 className="font-heading font-bold text-text-primary mb-2">Open install projects</h3>
            {projects === null && <p className="text-sm text-text-muted">Loading forecasts…</p>}
            {projects?.length === 0 && <p className="text-sm text-text-muted" data-testid="no-projects">No open install projects with coordinates yet — sign a proposal to open one.</p>}
            <div className="grid md:grid-cols-2 gap-3">
              {projects?.map(p => (
                <button key={p.id} onClick={() => getPlan({ project_id: p.id })} className="text-left p-4 rounded-xl border border-border bg-surface hover:border-primary/40 transition-colors" data-testid={`project-plan-${p.id}`}>
                  <div className="flex items-center justify-between"><b className="text-sm text-text-primary">{p.lead_name}</b><span className="badge bg-surface-alt text-text-secondary border-border capitalize">{p.stage}</span></div>
                  <p className="text-xs text-text-muted">{p.address}, {p.city} {p.state}{p.target_install_date ? ` · install ${p.target_install_date}` : ''}</p>
                  <div className="flex gap-1 mt-2">{p.days.map(d => <span key={d.date} title={`${d.date} · ${d.summary}`} className={`h-2 flex-1 rounded ${STATUS[d.status]}`} />)}</div>
                  <p className="text-[11px] text-text-muted mt-1">{p.go_days} go · {p.hold_days} hold · best {p.best_install_days.join(', ') || '—'}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-2" data-testid="history-panel">
          {!history.length && <p className="text-sm text-text-muted">No storm outreach sent yet.</p>}
          {history.map(h => (
            <div key={h.id} className="p-3 rounded-xl border border-border bg-surface text-xs flex flex-wrap gap-2 items-center" data-testid={`history-${h.id}`}>
              <span className="badge bg-surface-alt border-border text-text-secondary uppercase">{h.channel}</span><b>{h.lead_name}</b><span className="text-text-muted">→ {h.to}</span>
              <span className="text-text-muted">· {h.event} · {h.template}</span>{h.result?.simulated && <span className="badge bg-amber-50 text-amber-700 border-amber-200">SIMULATED</span>}
              <span className="ml-auto text-text-muted">{new Date(h.created_at).toLocaleString()}</span>
              <p className="w-full text-text-muted line-clamp-1">{h.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
